import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';

const TOAST_DURATION_MS = 3000;

const VARIANTS = {
  success: {
    wrapper: 'bg-green-50 border-green-200 text-green-800',
    icon: 'text-green-500',
    Icon: CheckCircle,
    role: 'status',
  },
  error: {
    wrapper: 'bg-red-50 border-red-200 text-red-800',
    icon: 'text-red-500',
    Icon: AlertCircle,
    role: 'alert',
  },
};

export const AdminToast = ({ toast, onClose, duration = TOAST_DURATION_MS }) => {
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [toast, onClose, duration]);

  if (!toast) return null;

  const { wrapper, icon, Icon, role } = VARIANTS[toast.type] || VARIANTS.success;

  return (
    <div className="fixed top-4 right-4 z-[80] max-w-sm w-[calc(100%-2rem)] sm:w-auto pointer-events-none">
      <div
        role={role}
        className={`pointer-events-auto flex items-start rounded-xl border p-4 shadow-lg ${wrapper}`}
      >
        <Icon className={`h-5 w-5 flex-shrink-0 ${icon}`} />
        <p className="ml-3 text-sm font-medium">{toast.message}</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss notification"
          className="ml-3 flex-shrink-0 opacity-60 hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const useAdminToast = () => {
  const [toast, setToast] = useState(null);

  const clearToast = useCallback(() => setToast(null), []);
  const showSuccess = useCallback((message) => setToast({ type: 'success', message, id: Date.now() }), []);
  const showError = useCallback((message) => setToast({ type: 'error', message, id: Date.now() }), []);

  const toastElement = <AdminToast toast={toast} onClose={clearToast} />;

  return { showSuccess, showError, clearToast, toastElement };
};
